import * as React from "react";
import { Modal, Button, Form } from "semantic-ui-react";
import { Input } from "@neworbit/simpleui-input";
import { connect } from "react-redux";

import { Link } from "@teamprevent/common/task/taskActions/models";

import { useDispatch } from "@teamprevent/admin-redux";

import {
    AdminUserListModelWithTeams,
    AppState as AdminUserListModelWithTeamsState,
    getUserDisplayNameWithTeamAndProfession
} from "../../crud/adminUserWithTeams/model";
import { assignUsersToActionableTasks } from "../actions";
import { SelectableTask } from "../models";
import { adminUsersWithTeamsSelector } from "../../crud/adminUserWithTeams/selectors";
import { loadAdminUsersWithTeams as loadAdminUsersAction } from "../../crud/adminUserWithTeams";
import { getUserSortableName } from "../../crud/adminUser/model";

type OwnProps = {
    tasks: SelectableTask[];
    open: boolean;
    onSaved: (assignedUsers: Link[]) => void;
    onCanceled: () => void;
}

interface StateProps {
    adminUsersWithTeams: AdminUserListModelWithTeams[];
}

interface DispatchProps {
    loadAdminUsers: () => void;
}

export const ActionableTaskAssignUnconnected:
    React.FunctionComponent<OwnProps & StateProps & DispatchProps> = ({ open, adminUsersWithTeams, loadAdminUsers, onSaved, onCanceled, tasks }) => {
        const [submitted, setSubmitted] = React.useState(false);
        const [saving, setSaving] = React.useState(false);
        const [userIds, setUserIds] = React.useState<string[]>([]);
        const dispatch = useDispatch();

        React.useEffect(() => {
            if (!adminUsersWithTeams.length) {
                loadAdminUsers();
            }
        }, [adminUsersWithTeams, loadAdminUsers]);

        const onSave = React.useCallback(async () => {
            setSubmitted(true);
            setSaving(true);

            const selectedUsers = adminUsersWithTeams.filter(au => userIds.includes(au.id)).map(adminUser => {
                return {
                    id: adminUser.id,
                    name: getUserDisplayNameWithTeamAndProfession(adminUser)
                };
            });

            await dispatch(assignUsersToActionableTasks(tasks, selectedUsers));

            setUserIds([]);
            onSaved(selectedUsers);
            setSaving(false);
        }, [adminUsersWithTeams, dispatch, onSaved, tasks, userIds]);

        const onCancel = React.useCallback(() => {
            setUserIds([]);
            onCanceled();
        }, [onCanceled]);

        const getAdminUserOptions = () => {
            if (!adminUsersWithTeams?.length) {
                return [];
            }

            const adminUsersWithTeamsSorted = [...adminUsersWithTeams.sort((a, b) => getUserSortableName(a).localeCompare(getUserSortableName(b)))];

            return adminUsersWithTeamsSorted;
        };

        return (
            <Modal open={open} size="small" closeOnDimmerClick={false}>
                <Modal.Header>Assign user</Modal.Header>
                <Modal.Content>
                    <Form>
                        <Input.DropdownMulti
                            dynamicOptions
                            value={userIds}
                            label="Users"
                            showErrors={submitted}
                            onChange={setUserIds}
                            options={getAdminUserOptions().map(adminUser =>
                                ({ key: adminUser.id, value: adminUser.id, text: getUserDisplayNameWithTeamAndProfession(adminUser) }))}
                            search
                        />
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={onCancel}>Cancel</Button>
                    <Button loading={saving} disabled={saving} content="Save" onClick={onSave} />
                </Modal.Actions>
            </Modal>
        );
    };

function mapStateToProps(state: AdminUserListModelWithTeamsState): StateProps {
    return {
        adminUsersWithTeams: adminUsersWithTeamsSelector(state)
    };
};

const mapDispatchToProps = (dispatch: any): DispatchProps => {
    return {
        loadAdminUsers: () => dispatch(loadAdminUsersAction())
    };
};

export const ActionableTaskAssign = connect(mapStateToProps, mapDispatchToProps)(ActionableTaskAssignUnconnected);
